import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../utils/api';
import QuestionEditModal from './QuestionEditModal';

const PROMPT_PLACEHOLDERS = ['{question}', '{response}'];

const AssessmentList = ({ refresh }) => {
  const { t } = useTranslation();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoGradingEnabled, setAutoGradingEnabled] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState('');
  const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState(15);
  const [originalTimePerQuestionMinutes, setOriginalTimePerQuestionMinutes] = useState(15);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  // Question edit modal state
  const [questionEditOpen, setQuestionEditOpen] = useState(false);
  const [assessmentToEdit, setAssessmentToEdit] = useState(null);

  const loadAssessments = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminAPI.getAssessments();
      setAssessments(data.assessments);
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssessments();
  }, [refresh]);

  const handleRowClick = (assessment) => {
    setSelectedAssessment(assessment);
    setAutoGradingEnabled(assessment.auto_grading_enabled);
    const initialPrompt = assessment.system_prompt || '';
    setSystemPrompt(initialPrompt);
    setOriginalSystemPrompt(initialPrompt);
    const initialMinutes = typeof assessment.time_per_question_minutes === 'number'
      ? assessment.time_per_question_minutes
      : 15;
    setTimePerQuestionMinutes(initialMinutes);
    setOriginalTimePerQuestionMinutes(initialMinutes);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAssessment(null);
    setSystemPrompt('');
    setOriginalSystemPrompt('');
    setError('');
  };

  const handleEditQuestions = (e, assessment) => {
    e.stopPropagation();
    setAssessmentToEdit(assessment);
    setQuestionEditOpen(true);
  };

  const handleQuestionEditClose = () => {
    setQuestionEditOpen(false);
    setAssessmentToEdit(null);
  };

  const handleQuestionsSaved = () => {
    loadAssessments();
  };

  const handleLoadDefaultPrompt = async () => {
    setLoadingDefault(true);
    setError('');
    try {
      const data = await adminAPI.getDefaultSystemPrompt();
      setSystemPrompt(data.template || '');
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoadingDefault(false);
    }
  };

  const handleResetPrompt = () => {
    setSystemPrompt('');
  };

  const handleSave = async () => {
    setError('');

    const trimmedPrompt = systemPrompt.trim();
    if (trimmedPrompt && PROMPT_PLACEHOLDERS.some((ph) => !trimmedPrompt.includes(ph))) {
      setError(t('admin.assessment.systemPromptPlaceholdersRequired'));
      return;
    }

    const parsedMinutes = Number(timePerQuestionMinutes);
    if (!Number.isInteger(parsedMinutes) || parsedMinutes < 1 || parsedMinutes > 240) {
      setError(t('admin.assessment.timePerQuestionInvalid'));
      return;
    }

    setSaving(true);

    try {
      const payload = { auto_grading_enabled: autoGradingEnabled };
      if (systemPrompt !== originalSystemPrompt) {
        // Empty string signals reset-to-default on the backend (stored as NULL)
        payload.system_prompt = trimmedPrompt;
      }
      if (parsedMinutes !== originalTimePerQuestionMinutes) {
        payload.time_per_question_minutes = parsedMinutes;
      }
      await adminAPI.updateAssessment(selectedAssessment.id, payload);

      await loadAssessments();
      handleCloseModal();
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('admin.assessment.title', 'Assessments')}
      </Typography>

      {error && !modalOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assessments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.assessment.noAssessments', 'No assessments created yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('admin.assessment.name', 'Name')}</strong></TableCell>
                <TableCell><strong>{t('admin.assessment.description', 'Description')}</strong></TableCell>
                <TableCell align="center"><strong>{t('admin.assessment.questions', 'Questions')}</strong></TableCell>
                <TableCell align="center"><strong>{t('admin.assessment.autoGradingColumn', 'Auto-Grade')}</strong></TableCell>
                <TableCell><strong>{t('admin.assessment.created', 'Created')}</strong></TableCell>
                <TableCell align="center"><strong>{t('admin.assessment.actions', 'Actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assessments.map((assessment) => (
                <TableRow
                  key={assessment.id}
                  onClick={() => handleRowClick(assessment)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <TableCell>{assessment.name}</TableCell>
                  <TableCell>{assessment.description || '-'}</TableCell>
                  <TableCell align="center">{assessment.question_count}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={assessment.auto_grading_enabled ? t('common.enabled', 'Enabled') : t('common.disabled', 'Disabled')}
                      color={assessment.auto_grading_enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(assessment.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('admin.questions.editQuestions', 'Edit Questions')}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleEditQuestions(e, assessment)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assessment Detail Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('admin.assessment.details', 'Assessment Details')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {selectedAssessment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>{t('admin.assessment.name', 'Name')}:</strong> {selectedAssessment.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('admin.assessment.description', 'Description')}:</strong> {selectedAssessment.description || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('admin.assessment.questions', 'Questions')}:</strong> {selectedAssessment.question_count}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('admin.assessment.created', 'Created')}:</strong> {new Date(selectedAssessment.created_at).toLocaleDateString()}
              </Typography>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoGradingEnabled}
                      onChange={(e) => setAutoGradingEnabled(e.target.checked)}
                    />
                  }
                  label={t('admin.assessment.autoGradingEnabled', 'Enable Auto-Grading')}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  {t('admin.assessment.autoGradingHelp', 'When enabled, new recordings will be automatically graded by the LLM after video processing completes.')}
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.assessment.timePerQuestion')}
                  value={timePerQuestionMinutes}
                  onChange={(e) => setTimePerQuestionMinutes(e.target.value)}
                  inputProps={{ min: 1, max: 240, step: 1 }}
                  helperText={t('admin.assessment.timePerQuestionHelp')}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label={t('admin.assessment.systemPrompt')}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  multiline
                  rows={14}
                  helperText={t('admin.assessment.systemPromptHelp')}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={handleLoadDefaultPrompt}
                    disabled={loadingDefault || systemPrompt.trim().length > 0}
                  >
                    {loadingDefault
                      ? t('admin.assessment.loadingDefaultPrompt')
                      : t('admin.assessment.loadDefaultPrompt')}
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    onClick={handleResetPrompt}
                    disabled={systemPrompt.length === 0}
                  >
                    {t('admin.assessment.resetPromptToDefault')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
          >
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Edit Modal */}
      <QuestionEditModal
        open={questionEditOpen}
        onClose={handleQuestionEditClose}
        assessment={assessmentToEdit}
        onSave={handleQuestionsSaved}
      />
    </Paper>
  );
};

export default AssessmentList;
